<div class="w-full mx-auto p-5 space-y-6">

    <!-- Header -->
    <div class="flex flex-wrap justify-between items-center gap-4">
        <div>
            <h1 class="text-2xl font-bold text-gray-800">Aprobaciones Pendientes</h1>
            <p class="text-gray-500 mt-1">Comprobantes de ingreso editados que requieren aprobación</p>
        </div>
        <div class="flex items-center gap-3">
            <button (click)="loadPendingApprovals()"
                class="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Actualizar
            </button>
        </div>
    </div>

    <!-- Banner info -->
    <div class="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <svg class="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div>
            <p class="text-sm font-medium text-amber-800">Los siguientes comprobantes fueron editados y requieren
                aprobación para que los cambios sean efectivos.</p>
            <p class="text-xs text-amber-600 mt-1">{{ receipts.length }} comprobante(s) pendiente(s) de revisión</p>
        </div>
    </div>

    <!-- Loading -->
    <div *ngIf="isLoading" class="flex justify-center items-center py-20">
        <div class="flex flex-col items-center gap-3">
            <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p class="text-gray-500 text-sm">Cargando comprobantes pendientes...</p>
        </div>
    </div>

    <!-- Error -->
    <div *ngIf="loadError && !isLoading" class="flex justify-center items-center py-20">
        <div class="flex flex-col items-center gap-3 text-center">
            <svg class="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-gray-600 font-medium">Error al cargar los comprobantes</p>
            <button (click)="loadPendingApprovals()" class="text-blue-600 hover:text-blue-800 text-sm underline">
                Reintentar
            </button>
        </div>
    </div>

    <!-- Empty state -->
    <div *ngIf="!isLoading && !loadError && receipts.length === 0" class="flex justify-center items-center py-20">
        <div class="flex flex-col items-center gap-3 text-center">
            <svg class="w-16 h-16 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-gray-600 font-medium text-lg">¡Todo al día!</p>
            <p class="text-gray-400 text-sm">No hay comprobantes pendientes de aprobación</p>
        </div>
    </div>

    <!-- Lista de comprobantes pendientes -->
    <div *ngIf="!isLoading && !loadError && receipts.length > 0" class="space-y-4">
        <div *ngFor="let receipt of receipts"
            class="bg-white border border-amber-200 rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 space-y-4">

            <!-- Header del comprobante -->
            <div class="flex justify-between items-start">
                <div>
                    <div class="flex items-center gap-3">
                        <h3 class="text-lg font-semibold text-gray-800">Comprobante #{{ receipt.id }}</h3>
                        <span
                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <svg class="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Pendiente de Aprobación
                        </span>
                        <span *ngIf="receipt.version"
                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            v{{ receipt.version }}
                        </span>
                    </div>
                    <p class="text-sm text-gray-500 mt-1">{{ receipt.projectName || receipt.project?.name || 'Sin
                        proyecto' }}</p>
                </div>
                <div class="text-right">
                    <p class="text-xs text-gray-400">Solicitado</p>
                    <p class="text-sm text-amber-700 font-medium">{{ getTimeAgo(receipt.approvalRequestedAt) }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">por {{ receipt.approvalRequestedBy || 'N/A' }}</p>
                </div>
            </div>

            <!-- Datos del comprobante -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wider">Pagador</p>
                    <p class="text-sm font-medium text-gray-800 mt-1">{{ getPayerName(receipt) }}</p>
                    <p class="text-xs text-gray-500" *ngIf="getPayerDocument(receipt)">{{ getPayerDocument(receipt) }}
                    </p>
                </div>
                <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wider">Monto</p>
                    <p class="text-sm font-bold text-green-700 mt-1">{{ formatCurrency(receipt.totalNet ||
                        receipt.totalAmount) }}</p>
                </div>
                <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wider">Método de Pago</p>
                    <p class="text-sm text-gray-800 mt-1">{{ receipt.paymentMethodName || receipt.paymentMethod?.name ||
                        'N/A' }}</p>
                </div>
                <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wider">Banco</p>
                    <p class="text-sm text-gray-800 mt-1">{{ receipt.bankName || receipt.bank?.name || 'N/A' }}</p>
                </div>
            </div>

            <!-- Justificación de la edición -->
            <div *ngIf="receipt.editJustification || receipt.approvalNotes"
                class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p class="text-xs text-blue-600 font-semibold uppercase tracking-wider mb-1">
                    <svg class="w-3.5 h-3.5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Justificación de la edición
                </p>
                <p class="text-sm text-blue-800">{{ receipt.editJustification || receipt.approvalNotes || 'Sin
                    justificación proporcionada' }}</p>
            </div>

            <!-- Acciones -->
            <div class="flex justify-end items-center gap-3 pt-2 border-t border-gray-100">
                <button (click)="viewDetails(receipt)"
                    class="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 transition">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Ver Detalles
                </button>
                <button (click)="showApprovalModal(receipt, 'Reject')"
                    class="bg-orange-50 text-orange-700 border border-orange-300 px-4 py-2 rounded-lg hover:bg-orange-100 transition flex items-center gap-2 text-sm font-medium">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Rechazar
                </button>
                <button (click)="showApprovalModal(receipt, 'Approve')"
                    class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2 text-sm font-medium">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Aprobar
                </button>
            </div>
        </div>
    </div>
</div>

<!-- Modal de confirmación de aprobación/rechazo -->
<div *ngIf="showApprovalConfirmationModal"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">

        <!-- Header del modal -->
        <div class="p-6 border-b"
            [ngClass]="approvalAction === 'Approve' ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'">
            <div class="flex items-center gap-3">
                <div class="p-2 rounded-full"
                    [ngClass]="approvalAction === 'Approve' ? 'bg-green-100' : 'bg-orange-100'">
                    <svg *ngIf="approvalAction === 'Approve'" class="w-6 h-6 text-green-600" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <svg *ngIf="approvalAction === 'Reject'" class="w-6 h-6 text-orange-600" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
                <div>
                    <h3 class="text-lg font-bold"
                        [ngClass]="approvalAction === 'Approve' ? 'text-green-800' : 'text-orange-800'">
                        {{ approvalAction === 'Approve' ? 'Confirmar Aprobación' : 'Confirmar Rechazo' }}
                    </h3>
                    <p class="text-sm" [ngClass]="approvalAction === 'Approve' ? 'text-green-600' : 'text-orange-600'">
                        Comprobante #{{ selectedReceipt?.id }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Cuerpo del modal -->
        <div class="p-6 space-y-4">

            <!-- Info del comprobante -->
            <div class="bg-gray-50 rounded-lg p-4 space-y-2">
                <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Pagador</span>
                    <span class="text-sm font-medium text-gray-800">{{ getPayerName(selectedReceipt) }}</span>
                </div>
                <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Monto</span>
                    <span class="text-sm font-bold text-green-700">{{ formatCurrency(selectedReceipt?.totalNet ||
                        selectedReceipt?.totalAmount) }}</span>
                </div>
                <div *ngIf="selectedReceipt?.editJustification" class="flex justify-between">
                    <span class="text-sm text-gray-500">Justificación</span>
                    <span class="text-sm text-gray-800 text-right max-w-[60%]">{{ selectedReceipt.editJustification
                        }}</span>
                </div>
            </div>

            <!-- Aviso -->
            <div class="text-sm" [ngClass]="approvalAction === 'Approve' ? 'text-green-700' : 'text-orange-700'">
                <span *ngIf="approvalAction === 'Approve'">
                    Al aprobar, los cambios editados serán aplicados al comprobante original.
                </span>
                <span *ngIf="approvalAction === 'Reject'">
                    Al rechazar, los cambios serán descartados. Debes proporcionar una razón.
                </span>
            </div>

            <!-- Notas -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                    Notas {{ approvalAction === 'Reject' ? '(obligatorio)' : '(opcional)' }}
                </label>
                <textarea [(ngModel)]="approvalNotes" rows="3"
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:border-transparent transition"
                    [ngClass]="approvalAction === 'Approve' ? 'focus:ring-green-500' : 'focus:ring-orange-500'"
                    [placeholder]="approvalAction === 'Approve' ? 'Notas adicionales sobre la aprobación...' : 'Razón del rechazo...'"></textarea>
            </div>
        </div>

        <!-- Footer del modal -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button (click)="onCancelApproval()" [disabled]="isProcessingApproval"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50">
                Cancelar
            </button>
            <button (click)="onConfirmApproval()"
                [disabled]="isProcessingApproval || (approvalAction === 'Reject' && !approvalNotes.trim())"
                class="px-5 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                [ngClass]="approvalAction === 'Approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'">
                <div *ngIf="isProcessingApproval" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white">
                </div>
                {{ isProcessingApproval ? 'Procesando...' : (approvalAction === 'Approve' ? 'Confirmar Aprobación' :
                'Confirmar Rechazo') }}
            </button>
        </div>
    </div>
</div>